import React, { useEffect, useMemo, useState } from 'react';
import { EditorAppSDK } from '@contentful/app-sdk';
import { useSDK } from '@contentful/react-apps-toolkit';
import { Grid, Box, Heading, Checkbox, Note, SkeletonContainer, SkeletonBodyText } from '@contentful/f36-components';
import { ContentfulRepository } from '../data/contentfulRepository';
import Preview from '../components/Preview';
import ProductsSelector from '../components/ProductsSelector';
import { ProductsCsvInput } from '../components/ProductsCsvInput';
import { ProductSelectorModeSwitch } from '../components/ProductSelectorModeSwitch';
import {
  CONTENTFUL_FIELD_SCOPE_DEFINITION,
  EMPTY_APPLICATION_STATE,
  EMPTY_SCOPE_DEFINITION as INITIAL_SCOPE_DEFINITION,
} from '../constants';
import { CategoriesSelector } from '../components/CategoriesSelector';
import { AttributesSelector } from '../components/AttributesSelector';
import { useCimpressAuthToken } from '@vp/contentful-app/auth';
import { MsxProduct } from '../clients/MsxService';
import useAttributes from '../hooks/useAttributes';
import useProducts from '../hooks/useProducts';
import useCategories from '../hooks/useCategories';
import useAlgoliaFilter from '../hooks/useAlgoliaFilter';
import useTags from '../hooks/useTags';
import { mapToScopeDefinition } from '../mappers/applicationState';
import { mapToApplicationState } from '../mappers/scopeDefinition';
import { ApplicationState } from '../model/ApplicationState';
import { Category } from '../model/Category';
import { ScopeDefinition } from '../model/ScopeDefinition';
import { UseOldScopeSwitch } from '../components/UseOldScopeSwitch';
import { LocaleSelector } from '../components/LocaleSelector';
import { TagsSelector } from '../components/TagsSelector';
import { MoqSelector } from '../components/MoqSelector';
import DebugData from '../components/DebugData';
import AlgoliaFilterErrorMessage from '../components/AlgoliaFilterErrorMessage';
import { getTenant } from '../clients/TenantProvider';

const Entry = () => {
  const sdk = useSDK<EditorAppSDK>();
  const contentfulRepository = new ContentfulRepository(sdk);
  const authToken = useCimpressAuthToken();

  const [tenant, setTenant] = useState<string | null>(null);
  const [isLoadingTenant, setIsLoadingTenant] = useState<boolean>(true);
  const [appState, setAppState] = useState<ApplicationState>(EMPTY_APPLICATION_STATE);
  const [scopeDefinition, setScopeDefinition] = useState<ScopeDefinition>(INITIAL_SCOPE_DEFINITION);
  const [showDebugData, setShowDebugData] = useState(false);

  const locale = contentfulRepository.getDefaultLocaleForSpace();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const availableLocales = useMemo(() => contentfulRepository.getAvailableLocales(), []);
  const [selectedLocale, setSelectedLocale] = useState<string>(locale);

  const { mappedAttributes, isLoadingAttributes } = useAttributes(tenant);
  const { products, isLoadingProducts } = useProducts(tenant, locale);
  const { categories, isLoadingCategories } = useCategories(tenant, locale);
  const { algoliaFilter, errorGeneratingAlgoliaFilter } = useAlgoliaFilter(
    authToken,
    scopeDefinition,
    selectedLocale,
    tenant
  );
  const { tags: availableTags, isLoadingTags } = useTags(tenant, locale);

  useEffect(() => {
    const resolveTenant = async () => {
      setIsLoadingTenant(true);
      const resolvedTenant = await getTenant(sdk.ids.space);
      setTenant(resolvedTenant);
      setIsLoadingTenant(false);
    };
    resolveTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const storedScopeDefinition =
      contentfulRepository.getEntryFieldValue<ScopeDefinition>(CONTENTFUL_FIELD_SCOPE_DEFINITION) ??
      INITIAL_SCOPE_DEFINITION;

    const applicationState = mapToApplicationState(storedScopeDefinition);
    setAppState(applicationState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const scopeDefinition = mapToScopeDefinition(appState);
    setScopeDefinition(scopeDefinition);
  }, [appState]);

  useEffect(() => {
    saveEntryData(scopeDefinition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeDefinition]);

  const handleSelectProducts = (product: MsxProduct) => {
    setAppState({
      ...appState,
      products: [...appState.products, product],
    });
  };

  const handleRemoveProducts = (product: MsxProduct) => {
    setAppState({
      ...appState,
      products: appState.products.filter((item) => item.mpvId !== product.mpvId),
    });
  };

  const handleSelectAttribute = (attributeName: string) => {
    setAppState({
      ...appState,
      attributes: [...appState.attributes, { name: attributeName, values: [] }],
    });
  };

  const handleRemoveAttribute = (attributeName: string) => {
    setAppState({
      ...appState,
      attributes: appState.attributes.filter((item) => item.name !== attributeName),
    });
  };

  const handleSelectAttributeValue = (attributeName: string, attributeValue: string) => {
    const attribute = appState.attributes.find((attribute) => attribute.name === attributeName);
    if (attribute) {
      const newAttribute = { ...attribute, values: [...(attribute?.values ?? []), attributeValue] };
      const newAttributes = appState.attributes.map((att) => {
        if (attributeName === att.name) {
          return newAttribute;
        } else {
          return att;
        }
      });
      setAppState({
        ...appState,
        attributes: newAttributes,
      });
    }
  };

  const handleRemoveAttributeValue = (attributeName: string, attributeValue: string) => {
    const attribute = appState.attributes.find((attribute) => attribute.name === attributeName);
    if (attribute) {
      const newValuesArray = attribute.values.filter((item) => item !== attributeValue);
      const newAttribute = { ...attribute, values: newValuesArray };
      const newAttributes = appState.attributes.map((att) => {
        if (attributeName === att.name) {
          return newAttribute;
        } else {
          return att;
        }
      });
      setAppState({
        ...appState,
        attributes: newAttributes,
      });
    }
  };

  const handleSelectCategory = (category: Category) => {
    setAppState({
      ...appState,
      categories: [...appState.categories, category],
    });
  };

  const handleRemoveCategory = (category: Category) => {
    setAppState({
      ...appState,
      categories: appState.categories.filter((item) => item.id !== category.id),
    });
  };

  const handleSelectTag = (tagName: string) => {
    setAppState({
      ...appState,
      tags: [...appState.tags, { name: tagName, value: true }],
    });
  };

  const handleRemoveTag = (tagName: string) => {
    setAppState({
      ...appState,
      tags: appState.tags.filter((item) => item.name !== tagName),
    });
  };

  const handleSelectTagValue = (tagName: string, value: boolean) => {
    setAppState({
      ...appState,
      tags: appState.tags.map((tag) => (tag.name === tagName ? { ...tag, value } : tag)),
    });
  };

  const handleChangedUseOldScope = (useOldScope: boolean) => {
    setAppState({
      ...appState,
      useOldScope,
    });
  };

  const handleChangedUseCsvProductInput = (useCsvProductInput: boolean) => {
    setAppState({
      ...appState,
      useCsvProductInput,
    });
  };

  const handleBulkSetProducts = (products: MsxProduct[]) => {
    setAppState({
      ...appState,
      products,
    });
  };

  const handleChangedMoq = (moq: boolean) => {
    setAppState({
      ...appState,
      moq,
    });
  };

  const saveEntryData = (scope: ScopeDefinition) => {
    if (scope !== INITIAL_SCOPE_DEFINITION) {
      contentfulRepository.setEntryFieldValue(CONTENTFUL_FIELD_SCOPE_DEFINITION, scope);
    }
  };

  if (isLoadingTenant) {
    return (
      <SkeletonContainer>
        <SkeletonBodyText numberOfLines={6} />
      </SkeletonContainer>
    );
  }

  if (tenant === null) {
    return (
      <Note variant="negative">
        Invalid tenant: the Contentful space could not be resolved to a known tenant. Please contact the{' '}
        <a
          href="https://vistaprint.atlassian.net/wiki/spaces/SESNAV/overview?homepageId=3269398397"
          target="_blank"
          rel="noreferrer"
        >
          Product Group Display team
        </a>
        .
      </Note>
    );
  }

  return (
    <Grid
      columns="1fr 1fr"
      rows="1fr 2fr 2fr"
      rowGap="spacingM"
      columnGap="spacingM"
      style={{ height: '100%', gridTemplateRows: 'auto' }}
    >
      <Grid.Item area="span 1 / span 2">
        <UseOldScopeSwitch useOldScope={appState.useOldScope} onChangeUseOldScope={handleChangedUseOldScope} />
      </Grid.Item>
      <Grid.Item area="span 1 / span 2">
        <LocaleSelector locales={availableLocales} selectedLocale={selectedLocale} onChangeLocale={setSelectedLocale} />
      </Grid.Item>
      <Grid.Item>
        <Box padding="spacingL">
          <Heading>Categories</Heading>
          <CategoriesSelector
            locale={locale}
            onSelectCategory={handleSelectCategory}
            onRemoveCategory={handleRemoveCategory}
            selectedCategories={appState.categories}
            categories={categories}
            isLoading={isLoadingCategories}
          />
        </Box>
        <Box padding="spacingL">
          <Heading>Product selector</Heading>
          <ProductSelectorModeSwitch
            useCsvProductInput={appState.useCsvProductInput}
            onChangeUseCsvProductInput={handleChangedUseCsvProductInput}
          />
          <Box marginTop="spacingS">
            {appState.useCsvProductInput ? (
              <ProductsCsvInput
                allProducts={products}
                defaultLocale={locale}
                isLoadingProducts={isLoadingProducts}
                selectedProducts={appState.products}
                onBulkSetProducts={handleBulkSetProducts}
              />
            ) : (
              <ProductsSelector
                locale={locale}
                onSelectProduct={handleSelectProducts}
                onRemoveProduct={handleRemoveProducts}
                selectedProducts={appState.products}
                products={products}
                isLoading={isLoadingProducts}
              />
            )}
          </Box>
        </Box>
        <Box padding="spacingL">
          <Heading>Tags selector</Heading>
          <TagsSelector
            onSelectTag={handleSelectTag}
            onRemoveTag={handleRemoveTag}
            onSelectTagValue={handleSelectTagValue}
            selectedTags={appState.tags}
            tags={availableTags}
            isLoading={isLoadingTags}
          />
        </Box>
        <Box padding="spacingL">
          <Heading>No MOQ</Heading>
          <MoqSelector moq={appState.moq} onChangeMoq={handleChangedMoq} />
        </Box>
        <Box padding="spacingL">
          <Heading>Attributes selector</Heading>
          <AttributesSelector
            onSelectAttribute={handleSelectAttribute}
            onRemoveAttribute={handleRemoveAttribute}
            onSelectAttributeValue={handleSelectAttributeValue}
            onRemoveAttributeValue={handleRemoveAttributeValue}
            selectedAttributes={appState.attributes}
            attributes={mappedAttributes}
            isLoading={isLoadingAttributes}
            locale={locale}
          />
        </Box>
        <AlgoliaFilterErrorMessage algoliaFilter={algoliaFilter} />
        <Checkbox
          name="show-debug-data"
          id="show-debug-data"
          isChecked={showDebugData}
          onChange={() => setShowDebugData(!showDebugData)}
        >
          Show debug data
        </Checkbox>
        {showDebugData && <DebugData scopeDefinition={scopeDefinition} algoliaFilter={algoliaFilter} />}
      </Grid.Item>
      <Grid.Item>
        <Box padding="spacingL">
          <Preview
            previewFiltersUrl={algoliaFilter}
            locale={selectedLocale}
            tenant={tenant}
            hasError={errorGeneratingAlgoliaFilter}
          />
        </Box>
      </Grid.Item>
    </Grid>
  );
};

export default Entry;
